import { Component, Inject, Input, OnInit, PLATFORM_ID, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SpecialModule } from 'lib/modules/special.module';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { CategoryService } from 'lib/services/category/category.service';
import { RouterService } from 'lib/services/router.service';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { WebpService } from 'lib/services/webp.service';
import { Location } from '@angular/common';
import { ProductSpecDialogComponent } from 'lib/components/product-spec-dialog/product-spec-dialog.component';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { WebstoreProductService } from 'lib/services/webstore-product/webstore-product.service';
import { TableComponent } from '../table/table.component';

const scriptName = 'cms-partial-category-top-component-product-options-2.component';

@Component({
    selector: 'cms-partial-category-top-component-product-options-2',
    templateUrl: './product-options-2.component.html',
    styleUrls: ['./product-options-2.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, SpecialModule, TableComponent],
})
export class ProductOptions2Component extends CmsPartialComponentClass implements OnInit {
    @Input() productOptionsRow: any;
    @ViewChild('tableComponent') tableComponent: TableComponent;
    getWebpImg: (src: string) => string;
    private xsBreakpointObserver: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);

    targetHighlights = ['Host Interface', 'Standard Memory', 'Power'];

    constructor(
        @Inject('environment') environment: any,
        breakpointObserver: BreakpointObserver,
        routerService: RouterService,
        webpService: WebpService,
        bsModalRef: BsModalRef,
        bsModalService: BsModalService,
        location: Location,
        categoryService: CategoryService,
        private productService: WebstoreProductService,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
        super({
            dependencies: {
                breakpointObserver,
                bsModalRef,
                bsModalService,
                environment,
                routerService,
                location,
                categoryService,
                webpService,
            },
        });
        this.getWebpImg = (src: string) => webpService.getWebpImg(src);
    }

    openProductSpecDialog(products?: any, options?: any) {
        try {
            const productsSpecs = new Set(['Product']);
            const productsValues = [];
            const productsLinks = [];
            if (products) {
                products.map((product: any) => {
                    const productValue = [];
                    productValue.push(product.title);
                    product.specs_group.map((specs) => {
                        productsSpecs.add(specs.spec);
                        productValue.push(specs.value);
                    });
                    productsValues.push(productValue);
                    productsLinks.push(product.product_link && product.product_link.routerLink);
                });
            }
            const productDialogRef = this.dialog.open(ProductSpecDialogComponent, {
                data: options
                    ? { options: options }
                    : {
                          specs: productsSpecs,
                          values: productsValues,
                          links: productsLinks,
                      },
                maxWidth: '1000px',
                panelClass: 'dialog-mob-only-product-spec',
            });
            const smallDialogSubscription = this.xsBreakpointObserver.subscribe((size) => {
                if (size.matches) {
                    productDialogRef.updateSize('100vw', '100vh');
                } else {
                    productDialogRef.updateSize('auto', 'auto');
                }
            });
            productDialogRef.afterClosed().subscribe(() => smallDialogSubscription.unsubscribe());
        } catch (err) {
            console.error(...new ExxComError(636423, scriptName, err).stamp());
        }
    }

    openTableModal() {
        if (this.tableComponent) {
            this.tableComponent.openModal();
        }
    }

    showTableInline(): boolean {
        try {
            const tableRow = this.get(this.productOptionsRow, 'product_options_2.product_options[0].tables[0]');
            if (!tableRow) return false;
            const tableType = this.get(tableRow, 'table_type');
            const maxCount = this.get(tableRow, 'new_table_format_2.row_count_cut_off', 0);
            const tableArray = this.get(tableRow, 'new_table_format_2.table_group[0].tables', []);
            let largestLength = 0;
            if (tableType === 'tabbed_and_stacked') {
                const tableGroups = this.get(tableRow, 'new_table_format_2.table_group', []);
                tableGroups.forEach((group) => {
                    let groupLength = 0;
                    group.tables?.forEach((t) => { groupLength += t?.table_reference[0]?.table?.rows?.length ?? 0; });
                    largestLength = Math.max(groupLength, largestLength);
                });
            } else if (tableType === 'stacked_table') {
                tableArray.forEach((t) => { largestLength += t?.table_reference[0]?.table?.rows?.length ?? 0; });
            } else {
                tableArray.forEach((t) => { largestLength = Math.max(t?.table_reference[0]?.table?.rows?.length ?? 0, largestLength); });
            }
            return largestLength <= maxCount;
        } catch (err) {
            console.error(...new ExxComError(738291, scriptName, err).stamp());
            return false;
        }
    }

    ngOnInit() {
        // Image lookups mutate the product data and can land after SSR serializes the page
        if (isPlatformBrowser(this.platformId)) {
            this.fetchProductImages();
        }
    }

    fetchProductImages() {
        this.productOptionsRow.product_options_2.product_options[0].feature_product.forEach(async (product) => {
            if (product.use_custom_image) return;
            const productData = await this.productService.getProductByUrlComponent(product.product_link.href);
            if (productData) {
                product.product_link.image_href = productData.imageUrl;
            }
        });
    }
}
